// App — entry-point do builder NanoApp.
//
// Estrutura:
//   - HashRouter (compatível com hosting estático em CF Pages)
//   - AuthProvider (chama /auth/me no boot)
//   - Rotas:
//     • /reset-password — pública, sem layout
//     • /, /apps, /collections, /integrations — dentro do Layout (sidebar)
//     • /builder/:id, /builder/:id/responses, /apps/:id, /collections/:id —
//       full-screen (sem sidebar)
//
// Code splitting (Sprint 6): pages pesadas (Builder, AppDetail,
// CollectionDetail, FieldPropertiesPanel via Builder, Responses) viram
// chunks separados via React.lazy. Bundle inicial fica ~70kB gzip
// (AuthScreen + Layout + Dashboard); demais pages baixam sob demanda.

import { lazy, Suspense, useEffect, useState, Component, type ReactNode } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Layout from './components/Layout';
import DialogHost from './components/Dialog';
import { ErrorBoundary } from './components/ErrorBoundary';
import AuthScreen from './pages/AuthScreen';
import FormsPage from './pages/FormsPage';
import { loadAppSettings, applyAppSettings } from './utils/appSettings';

// 2026-05-20 Phase 2.3: lazy com retry automático. CF Pages às vezes serve
// chunk stale após deploy (race entre cache invalidation + load). Sem retry,
// user via "tela branca/spinner infinito" e tinha que dar reload manual.
// Estratégia: tenta importar; se falhar, espera N*500ms e re-tenta. Max 2
// retries (total 3 tentativas). Se ainda falhar, ChunkErrorBoundary captura.
function lazyWithRetry<T extends React.ComponentType<unknown>>(
  importer: () => Promise<{ default: T }>,
  retries = 2,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: Error | null = null;
    for (let i = 0; i <= retries; i++) {
      try {
        return await importer();
      } catch (e) {
        lastError = e as Error;
        if (i < retries) {
          await new Promise((r) => setTimeout(r, 500 * (i + 1)));
        }
      }
    }
    throw lastError!;
  });
}

// Lazy: pages pesadas (~50% do bundle quando combinadas)
const Apps = lazyWithRetry(() => import('./pages/Apps'));
const AppDetail = lazyWithRetry(() => import('./pages/AppDetail'));
const Collections = lazyWithRetry(() => import('./pages/Collections'));
const CollectionsTrash = lazyWithRetry(() => import('./pages/CollectionsTrash'));
const CollectionDetail = lazyWithRetry(() => import('./pages/CollectionDetail'));
const Integrations = lazyWithRetry(() => import('./pages/Integrations'));
const Builder = lazyWithRetry(() => import('./pages/Builder'));
const Responses = lazyWithRetry(() => import('./pages/Responses'));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));
// 2026-05-18: Workspace de Clientes
const Home = lazyWithRetry(() => import('./pages/Home'));
const Customer = lazyWithRetry(() => import('./pages/Customer'));

function FullPageSpinner() {
  // 2026-05-20 Phase 2.1: timeout no spinner. Se chunk demora >15s, mostra
  // alerta "Conexão lenta" + botão recarregar em vez de spinner infinito.
  const [showSlowNetworkHint, setShowSlowNetworkHint] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowSlowNetworkHint(true), 15_000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-4 px-4">
      <span className="inline-block w-7 h-7 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
      {showSlowNetworkHint && (
        <div className="text-center max-w-xs">
          <p className="text-sm text-slate-700 mb-2">Conexão lenta…</p>
          <button
            onClick={() => window.location.reload()}
            className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-700"
          >
            Recarregar página
          </button>
        </div>
      )}
    </div>
  );
}

// 2026-05-20 Phase 2.1: captura erros de chunk loading (dynamic import).
// Sintomas reportados: tela branca após login OU "spinner infinito" em
// rede instável / após deploy do CF Pages com cache stale.
// Componente class porque getDerivedStateFromError + componentDidCatch
// não têm equivalente em hooks.
class ChunkErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  override componentDidCatch(error: Error) {
    // Log estruturado pra ajudar diagnóstico
    console.error('[ChunkErrorBoundary] chunk load failure:', error);
  }
  override render() {
    if (this.state.error) {
      const isChunkError = /chunk|loading|import|module/i.test(this.state.error.message);
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
          <div className="max-w-md text-center bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
            <p className="text-base font-semibold text-slate-900 mb-2">
              {isChunkError ? 'Falha ao carregar a página' : 'Erro inesperado'}
            </p>
            <p className="text-sm text-slate-600 mb-4">
              {isChunkError
                ? 'Pode ser cache antigo. Tente recarregar.'
                : 'Algo deu errado. Recarregue e tente novamente.'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium"
            >
              Recarregar
            </button>
            <p className="text-[10px] text-slate-400 mt-3 font-mono break-all">
              {this.state.error.message}
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function L({ children }: { children: React.ReactNode }) {
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<FullPageSpinner />}>{children}</Suspense>
    </ChunkErrorBoundary>
  );
}

// Wrapper de rota: ErrorBoundary que reseta quando o pathname muda. Sem isso,
// um erro persistia até reload manual (caso reportado: voltar do AppDetail).
function RouteBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (!user) return <AuthScreen />;
  return <>{children}</>;
}

function AppShell() {
  // Aplica app settings (logo, accent color, dark mode) no boot.
  useEffect(() => {
    applyAppSettings(loadAppSettings());
  }, []);

  return (
    <RouteBoundary>
      <Routes>
        {/* Public */}
        <Route path="/reset-password" element={<L><ResetPassword /></L>} />

        {/* Full-screen (sem sidebar) */}
        <Route path="/builder/:id/responses" element={<ProtectedRoute><L><Responses /></L></ProtectedRoute>} />
        <Route path="/builder/:id" element={<ProtectedRoute><L><Builder /></L></ProtectedRoute>} />
        <Route path="/apps/:id" element={<ProtectedRoute><L><AppDetail /></L></ProtectedRoute>} />
        {/* 2026-05-18: AppDetail em contexto de cliente (full-screen, mesma component;
            AppDetail lê o slug da URL via useParams pra breadcrumb). */}
        <Route path="/customers/:slug/apps/:id" element={<ProtectedRoute><L><AppDetail /></L></ProtectedRoute>} />
        <Route path="/collections/:id" element={<ProtectedRoute><L><CollectionDetail /></L></ProtectedRoute>} />

        {/* Dentro do Layout (sidebar). 2026-05-18: index é Home (Workspace de Clientes).
            Forms tem rota dedicada /forms. /dashboard fica como redirect → /forms por
            back-compat (links antigos, bookmarks). */}
        <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<L><Home /></L>} />
          <Route path="forms" element={<FormsPage />} />
          <Route path="dashboard" element={<Navigate to="/forms" replace />} />
          <Route path="customers/:slug" element={<L><Customer /></L>} />
          <Route path="apps" element={<L><Apps /></L>} />
          <Route path="collections" element={<L><Collections /></L>} />
          <Route path="collections/trash" element={<L><CollectionsTrash /></L>} />
          <Route path="integrations" element={<L><Integrations /></L>} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </RouteBoundary>
  );
}

export function App() {
  return (
    <HashRouter>
      <AuthProvider>
        <AppShell />
        <DialogHost />
      </AuthProvider>
    </HashRouter>
  );
}

export default App;
